import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
 TrendingUp, TrendingDown, Users, Clock, Wifi, Smartphone, FileText,
 ArrowRight, Plus, Receipt, MapPin, Activity, Server, Zap, ShieldAlert,
 Calendar, Eye
} from 'lucide-react';
import BranchHealthAlert from '../components/BranchHealthAlert';
import CustomerSegmentCards, { SecondaryKPIRow, VoucherKPIRow } from '../components/CustomerSegmentCards';
import RecentActivityCard from '../components/RecentActivityCard';
import { RadiusLinkBadge } from '../components/RadiusLinkBadge';
import {
 ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, LabelList,
 PieChart, Pie, Cell
} from 'recharts';

// Refined accent palette for dark theme (more muted/sophisticated)
const ACCENT = {
 blue: { hex: '#60a5fa', glow: 'rgba(96, 165, 250, 0.15)', border: 'rgba(96, 165, 250, 0.25)' },
 orange: { hex: '#fb923c', glow: 'rgba(251, 146, 60, 0.15)', border: 'rgba(251, 146, 60, 0.25)' },
 red: { hex: '#f87171', glow: 'rgba(248, 113, 113, 0.15)', border: 'rgba(248, 113, 113, 0.25)' },
 yellow: { hex: '#facc15', glow: 'rgba(250, 204, 21, 0.15)', border: 'rgba(250, 204, 21, 0.25)' },
 green: { hex: '#34d399', glow: 'rgba(52, 211, 153, 0.15)', border: 'rgba(52, 211, 153, 0.25)' },
 violet: { hex: '#a78bfa', glow: 'rgba(167, 139, 250, 0.15)', border: 'rgba(167, 139, 250, 0.25)' },
};

const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');
const fmtIDRShort = (n) => {
 const v = Number(n || 0);
 if (v >= 1e9) return 'Rp ' + (v / 1e9).toFixed(1) + 'M';
 if (v >= 1e6) return 'Rp ' + (v / 1e6).toFixed(1) + 'jt';
 if (v >= 1e3) return 'Rp ' + (v / 1e3).toFixed(0) + 'rb';
 return 'Rp ' + v;
};

// Premium dark KPI card with subtle accent glow
const KPICard = ({ title, value, icon: Icon, color, subtitle, trend, testid }) => {
 const c = ACCENT[color] || ACCENT.blue;
 const trendUp = trend && trend > 0;
 const trendDown = trend && trend < 0;
 return (
 <div data-testid={testid} className="relative rounded-xl p-4 transition-all duration-300 group cursor-default overflow-hidden"
 style={{
 background: 'rgba(30, 41, 59, 0.4)',
 backdropFilter: 'blur(12px)',
 border: '1px solid rgba(148, 163, 184, 0.1)',
 }}>
 {/* Glow accent on hover */}
 <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
 style={{ background: `radial-gradient(circle at top right, ${c.glow}, transparent 60%)` }} />
 <div className="relative z-10">
 <div className="flex items-start justify-between mb-3">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 leading-tight dark:text-slate-500">{title}</div>
 <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
 style={{ background: c.glow, border: `1px solid ${c.border}`, color: c.hex }}>
 <Icon className="w-4 h-4" />
 </div>
 </div>
 <div className="text-2xl sm:text-3xl font-bold text-white mb-0.5 truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
 {subtitle && <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>}
 {(trendUp || trendDown) && (
 <span className={`mt-2 inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full items-center gap-0.5 ${trendUp ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
 {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
 {Math.abs(trend).toFixed(1)}%
 </span>
 )}
 </div>
 </div>
 );
};

const QuickAction = ({ icon: Icon, label, to }) => (
 <Link to={to} className="flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all group min-w-0"
 style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(148, 163, 184, 0.12)' }}>
 <div className="w-7 h-7 rounded-md flex items-center justify-center text-slate-300 group-hover:text-white transition-colors flex-shrink-0"
 style={{ background: 'rgba(96, 165, 250, 0.1)' }}>
 <Icon className="w-4 h-4" />
 </div>
 <span className="text-xs font-semibold text-slate-200 group-hover:text-white truncate transition-colors">{label}</span>
 </Link>
);

const SectionCard = ({ title, icon: Icon, action, children, accent = 'blue' }) => {
 const c = ACCENT[accent] || ACCENT.blue;
 return (
 <div className="rounded-xl overflow-hidden"
 style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(12px)', border: '1px solid rgba(148, 163, 184, 0.1)' }}>
 <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.08)' }}>
 <div className="flex items-center gap-2.5">
 {Icon && (
 <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: c.glow, border: `1px solid ${c.border}`, color: c.hex }}>
 <Icon className="w-4 h-4" />
 </div>
 )}
 <h3 className="font-semibold text-slate-100 text-sm">{title}</h3>
 </div>
 {action}
 </div>
 <div className="p-4">{children}</div>
 </div>
 );
};

const DashboardDark = ({
 stats,
 routers,
 packageStats,
 registrations,
 revenueTrend,
 recentCustomers,
 serviceInfo,
 loading,
 selectedBranch,
}) => {
 const { user: _authUserHF } = useAuth();
 const _isTeknisi = (_authUserHF?.role || '').toLowerCase() === 'teknisi';
  // 2026-07-11: Informasi Layanan (subscription info) only for tenant admin roles
  const _isTenantAdmin = ['super_admin', 'superadmin', 'admin'].includes((_authUserHF?.role || '').toLowerCase());

 const incomeToday = stats?.income_today || 0;
 const incomeYesterday = stats?.income_yesterday || 0;
 const incomeChange = incomeYesterday > 0 ? ((incomeToday - incomeYesterday) / incomeYesterday) * 100 : 0;

 const totalCustomers = stats?.total_customers || 0;
 const activePppoe = stats?.pppoe_online || 0;
 const activeHotspot = stats?.hotspot_online || 0;
 const isolated = stats?.isolated_customers || 0;
 const pendingInvoices = stats?.pending_invoices || 0;
 const dueCustomers = stats?.due_customers || 0;

 const onlineRouters = routers.filter(r => r.status === 'online' || r.is_online).length;
 const totalRouters = routers.length;

 // Pie palette for dark mode
 const PIE_COLORS = [ACCENT.blue.hex, ACCENT.orange.hex, ACCENT.violet.hex, ACCENT.yellow.hex, ACCENT.green.hex, ACCENT.red.hex, '#22d3ee'];

 const RegTooltip = ({ active, payload }) => {
 if (!active || !payload || !payload[0]) return null;
 const p = payload[0].payload || {};
 const d = p.date || '';
 let label = d;
 try {
 const dt = new Date(d);
 if (!isNaN(dt)) label = dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
 } catch {}
 const count = p.count || 0;
 return (
 <div style={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', color: '#e2e8f0' }}>
 <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
 <div style={{ color: '#f1f5f9', fontWeight: 700 }}>
 <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#10b981', marginRight: 6 }} />
 {count} Registrasi
 </div>
 </div>
 );
 };
 const RegDot = (props) => {
 const { cx, cy, payload } = props;
 const v = payload?.count || 0;
 if (v === 0) return <circle cx={cx} cy={cy} r={2} fill={'#10b981'} fillOpacity={0.5} />;
 return <circle cx={cx} cy={cy} r={5} fill={'#10b981'} stroke={'#0f172a'} strokeWidth={2} />;
 };


 if (loading) {
 return (
 <div className="flex items-center justify-center min-h-[400px]" style={{ background: '#0b1120' }}>
 <div className="animate-spin rounded-full h-12 w-12 border-2" style={{ borderColor: ACCENT.blue.hex, borderTopColor: 'transparent' }}></div>
 </div>
 );
 }

 return (
 <div className="space-y-4 p-3 sm:p-5 lg:p-6 pt-2 sm:pt-3 lg:pt-3 min-h-screen relative" style={{ background: 'radial-gradient(ellipse at top, #1e293b 0%, #0b1120 60%)' }}>
 {/* Subtle grid bg */}
 <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

 <div className="relative z-10 space-y-4">
 <BranchHealthAlert selectedBranch={selectedBranch} variant="dark" />
 {/* HERO INCOME CARD - Glassmorphic dark with subtle blue */}
{!_isTeknisi && (
<div className="relative">
<RadiusLinkBadge />
<div className="relative rounded-2xl overflow-hidden"
style={{
background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.4) 0%, rgba(30, 41, 59, 0.6) 50%, rgba(30, 41, 59, 0.6) 100%)',
backdropFilter: 'blur(20px)',
border: '1px solid rgba(96, 165, 250, 0.2)',
boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
}}>
{/* Decorative orbs */}
<div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: ACCENT.blue.hex }} />
<div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ background: ACCENT.violet.hex }} />

<div className="relative p-5 sm:p-7">
<div className="flex items-center gap-2 mb-2">
<div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: 'rgba(250, 204, 21, 0.15)', color: ACCENT.yellow.hex }}>
<Zap className="w-4 h-4" />
</div>
<span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Pendapatan Hari Ini</span>
</div>
<div className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-3 tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="hero-income-today">
{fmtIDR(incomeToday)}
</div>
<div className="flex items-center gap-3 flex-wrap mb-5">
{incomeChange !== 0 && (
<span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${incomeChange > 0 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'}`}>
{incomeChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
{Math.abs(incomeChange).toFixed(1)}% vs kemarin
</span>
)}
<span className="text-xs text-slate-400 dark:text-slate-500">
Kemarin: <span className="text-slate-300 font-mono">{fmtIDR(incomeYesterday)}</span>
</span>
</div>
</div>
</div>
</div>

)}

 {/* CUSTOMER SEGMENT CARDS — TOTAL / ONLINE / OFFLINE */}
 <VoucherKPIRow stats={stats} />
 <CustomerSegmentCards stats={stats} variant='dark' />

 {/* SECONDARY KPI ROW — Belum Bayar / Jatuh Tempo / Diisolir */}
 <SecondaryKPIRow pendingInvoices={pendingInvoices} dueCustomers={dueCustomers} isolated={isolated} variant="dark" />

 {/* MAIN GRID */}
 <div className={`grid grid-cols-1 ${(_isTeknisi || !_isTenantAdmin) ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-4`}>
 {_isTenantAdmin && <SectionCard title="Informasi Layanan" icon={Activity} accent="blue">
 <div className="space-y-1.5">
 {[
 { label: 'Paket Subscription', value: serviceInfo?.subscription?.plan_name || stats?.subscription_plan || 'ENTERPRISE', sub: `Max ${(serviceInfo?.subscription?.max_customers ?? stats?.max_users ?? 5000).toLocaleString('id-ID')} users`, color: 'blue', icon: Server },
 { label: 'PPPoE Users', value: `${(serviceInfo?.usage?.pppoe_used ?? stats?.total_pppoe_customers ?? 0).toLocaleString('id-ID')}`, sub: `dari ${(serviceInfo?.subscription?.max_customers ?? 5000).toLocaleString('id-ID')}`, color: 'green', icon: Wifi },
 { label: 'Hotspot Users', value: `${(serviceInfo?.usage?.hotspot_used ?? activeHotspot ?? 0).toLocaleString('id-ID')}`, sub: `dari ${(serviceInfo?.subscription?.max_hotspot ?? 5000).toLocaleString('id-ID')}`, color: 'violet', icon: Smartphone },
 { label: 'MikroTik Devices', value: `${serviceInfo?.mikrotik?.used ?? onlineRouters} / ${serviceInfo?.mikrotik?.max ?? totalRouters ?? 20}`, sub: `Tersedia: ${serviceInfo?.mikrotik?.available ?? ((totalRouters || 20) - onlineRouters)}`, color: 'red', icon: Server },
 { label: 'Jatuh Tempo Subs', value: serviceInfo?.subscription?.subscription_end_date ? new Date(serviceInfo.subscription.subscription_end_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : (stats?.subscription_due_date || '-'), sub: serviceInfo?.subscription?.days_until_expiry != null ? `${serviceInfo.subscription.days_until_expiry} hari lagi` : (stats?.subscription_due_days != null ? `${stats.subscription_due_days} Hari` : '-'), color: 'orange', icon: Calendar },
 ].map((row, i) => {
 const rc = ACCENT[row.color] || ACCENT.blue;
 const RowIcon = row.icon;
 return (
 <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg transition-colors hover:bg-white/5">
 <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
 style={{ background: rc.glow, border: `1px solid ${rc.border}`, color: rc.hex }}>
 <RowIcon className="w-4 h-4" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-xs text-slate-300 font-medium">{row.label}</div>
 <div className="text-[11px] text-slate-500 dark:text-slate-400">{row.sub}</div>
 </div>
 <div className="text-sm font-semibold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{row.value}</div>
 </div>
 );
 })}
 </div>
 </SectionCard>}

 {!_isTeknisi && (
 <SectionCard title="Tren Pendapatan Tahun Ini" icon={TrendingUp} accent="blue"
 action={<Link to="/app/revenue" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors">Detail <ArrowRight className="w-3 h-3" /></Link>}>
 {revenueTrend.length > 0 ? (
 <div className="h-56">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={revenueTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" vertical={false} />
 <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
 <YAxis tickFormatter={fmtIDRShort} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
 <Tooltip formatter={(v) => fmtIDR(v)} contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, fontSize: 11, color: '#e2e8f0' }} />
 <Line type="monotone" dataKey="amount" stroke={ACCENT.blue.hex} strokeWidth={2.5} dot={{ fill: ACCENT.blue.hex, r: 4, strokeWidth: 2, stroke: '#0f172a' }} activeDot={{ r: 6, strokeWidth: 2, stroke: '#0f172a' }} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="h-56 flex items-center justify-center text-slate-500 text-sm dark:text-slate-400">Belum ada data pendapatan</div>
 )}
 </SectionCard>
 )}

 <SectionCard title="Status Router MikroTik" icon={Server} accent="green"
 action={<span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">{onlineRouters}/{totalRouters} Online</span>}>
 <div className="space-y-2 max-h-[460px] overflow-y-auto scrollbar-hide pr-1" data-testid="router-status-scrollable">
 {routers.length === 0 ? (
 <div className="text-center py-6 text-slate-500 text-sm dark:text-slate-400">Belum ada router terdaftar</div>
 ) : routers.map((r, i) => {
 const isOnline = r.status === 'online' || r.is_online;
 return (
 <div key={r.id || i} className="flex items-center gap-3 p-2.5 rounded-lg transition-all hover:bg-white/5"
 style={{ border: '1px solid rgba(148, 163, 184, 0.08)' }}>
 <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`}></div>
 <Wifi className={`w-4 h-4 ${isOnline ? 'text-emerald-400' : 'text-slate-600'}`} />
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-slate-200 truncate">{r.name || r.host || 'Router'}</div>
 <div className="text-xs text-slate-500 truncate dark:text-slate-400">{r.host || r.ip_address} {r.location ? `• ${r.location}` : ''}</div>
 </div>
 {typeof r.active_sessions === 'number' && (
 <span
 className="relative inline-flex items-center justify-center min-w-[52px] h-6 px-2 rounded-md bg-slate-800/70 border border-slate-600/40 mr-1 overflow-hidden"
 title={r.active_error || `${r.active_sessions} active PPPoE sessions`}
 data-testid={`router-active-${r.id || i}`}
 >
 <span className="relative z-10 text-xs font-bold text-white tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
 {(r.active_sessions || 0).toLocaleString('id-ID')}
 </span>
 </span>
 )}
 <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isOnline ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-700/40 text-slate-500'}`}>
 {isOnline ? 'Online' : 'Offline'}
 </span>
 </div>
 );
 })}
 
 </div>
 </SectionCard>
 </div>

 {/* SECONDARY GRID */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
 <SectionCard title="Distribusi Paket Pelanggan" icon={Activity} accent="violet">
 {packageStats.length > 0 ? (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="h-48">
 <ResponsiveContainer width="100%" height="100%">
 <PieChart>
 <Pie data={packageStats} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
 {packageStats.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#0f172a" strokeWidth={2} />)}
 </Pie>
 <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, fontSize: 11, color: '#e2e8f0' }} />
 </PieChart>
 </ResponsiveContainer>
 </div>
 <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
 {packageStats.map((p, i) => (
 <div key={i} className="flex items-center gap-2 text-xs">
 <div className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
 <span className="flex-1 text-slate-300 truncate">{p.name}</span>
 <span className="font-bold text-slate-100" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.count}</span>
 </div>
 ))}
 </div>
 </div>
 ) : (
 <div className="h-48 flex items-center justify-center text-slate-500 text-sm dark:text-slate-400">Belum ada data paket</div>
 )}
 </SectionCard>

 <SectionCard title="Aktivitas Registrasi (30 Hari)" icon={Calendar} accent="green">
 {registrations.length > 0 ? (
 <div className="h-48">
 <ResponsiveContainer width="100%" height="100%">
 <LineChart data={registrations.slice(-30)} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
 <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" vertical={false} />
 <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={4} tickFormatter={(d) => (d || '').substring(8) + '/' + (d || '').substring(5,7)} />
 <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
 <Tooltip content={<RegTooltip />} cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }} />
 <Line type="monotone" dataKey="count" stroke={ACCENT.green.hex} strokeWidth={2.5} dot={<RegDot />} activeDot={{ r: 7, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 ) : (
 <div className="h-48 flex items-center justify-center text-slate-500 text-sm dark:text-slate-400">Belum ada registrasi</div>
 )}
 </SectionCard>

 <RecentActivityCard variant="dark" />

 <SectionCard title="Pelanggan Terbaru" icon={Users} accent="orange"
 action={<Link to="/app/customers" className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1 transition-colors">Lihat Semua <ArrowRight className="w-3 h-3" /></Link>}>
 <div className="space-y-1.5">
 {recentCustomers.length === 0 ? (
 <div className="text-center py-6 text-slate-500 text-sm dark:text-slate-400">Belum ada pelanggan</div>
 ) : recentCustomers.slice(0, 6).map(c => (
 <Link key={c.id} to={`/app/customers/${c.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
 <div className="w-8 h-8 rounded-full text-white flex items-center justify-center font-bold text-sm flex-shrink-0"
 style={{ background: 'linear-gradient(135deg, #fb923c 0%, #f87171 100%)' }}>
 {(c.name || '?').charAt(0).toUpperCase()}
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-sm font-semibold text-slate-200 truncate">{c.name}</div>
 <div className="text-xs text-slate-500 truncate dark:text-slate-400">{c.service_plan_name || c.pppoe_username || '-'}</div>
 </div>
 <Eye className="w-4 h-4 text-slate-600 dark:text-slate-300" />
 </Link>
 ))}
 </div>
 </SectionCard>
 </div>
 </div>
 </div>
 );
};

export default DashboardDark;
